import { Injectable } from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { UserProfile } from '../user.service';

@Injectable({
    providedIn: 'root'
})
export class MockUserService {
    private mockProfile: UserProfile = {
        uid: 'test-user-id',
        email: 'test@example.com',
        displayName: 'Test User',
        familyId: 'mock-family-id',
        photoURL: 'assets/avatar-placeholder.png',
        state: 'Test State',
        country: 'Test Country'
    };

    // Observable support
    private currentUserProfileSubject: BehaviorSubject<UserProfile | null>;
    currentUserProfile$ = new BehaviorSubject<UserProfile | null>(null).asObservable();

    private readonly STORAGE_KEY = 'mock_user_profile';

    constructor() {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (stored) {
            this.mockProfile = JSON.parse(stored);
        }
        this.currentUserProfileSubject = new BehaviorSubject<UserProfile | null>(this.mockProfile);
        this.currentUserProfile$ = this.currentUserProfileSubject.asObservable();
    }

    private saveToStorage() {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.mockProfile));
    }

    get currentProfileSnapshot() {
        return this.mockProfile;
    }

    getProfile(uid: string) {
        // Always return the mock profile regardless of ID
        return Promise.resolve(this.mockProfile);
    }

    async updateFamilyId(uid: string, familyId: string) {
        if (this.mockProfile.uid === uid) {
            this.mockProfile.familyId = familyId;
            this.saveToStorage();
            this.currentUserProfileSubject.next({ ...this.mockProfile });
        }
        return Promise.resolve();
    }

    async lookupUserByEmail(email: string): Promise<UserProfile | null> {
        if (email === this.mockProfile.email) {
            return Promise.resolve(this.mockProfile);
        }
        return Promise.resolve(null);
    }

    async updateProfileData(uid: string, data: Partial<UserProfile>) {
        if (this.mockProfile.uid === uid) {
            this.mockProfile = { ...this.mockProfile, ...data };
            this.saveToStorage();
            this.currentUserProfileSubject.next({ ...this.mockProfile });
        }
        return Promise.resolve();
    }

    async deleteAccount(uid: string) {
        if (this.mockProfile.uid === uid) {
            localStorage.removeItem(this.STORAGE_KEY);
            this.currentUserProfileSubject.next(null);
        }
        return Promise.resolve();
    }
}
